import React, {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";

import { Navigate, useNavigate } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { SingleValue } from "react-select";
import DateRangePicker from "react-bootstrap-daterangepicker";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { DatePicker } from "antd";
import dayjs from "dayjs";

import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import Search from "../../../../formHelper/search";
import Pagination from "../../../../formHelper/pagination";
import Success from "../../../../formHelper/success";
import Delete from "../../../../formHelper/delete";
import FilterDropdown from "../../../../formHelper/filterDropDown";
import Table from "../../../../formHelper/list";
import QuillEditor from "../../../../formHelper/QuillEditor";
import Loader from "../../../../formHelper/Loader";
import Reason from "./reason";
import { parseValidationErrors } from "../../../../apiHelper/validationHelper";
import QuotationPreview from "./quotationPreview";
import OrderPreview from "../order/orderPreview";
import type { RootState } from "../../../../core/data/redux/store";
import { useSelector } from "react-redux";

/* --------- Types ---------- */
type Id = number;

interface Branch {
  id: Id;
  name: string;
}
interface Option {
  value: Id;
  label: string;
  [k: string]: any;
}
interface CustomerDD {
  id: Id;
  company_name: string;
  owner_name: string;
  address: string;
  state_id: Id;
  other_state: any;
  city: any;
  mobile_no: any;
  email_id: any;
  landline_no: any;
  pin_code: any;
  customer_name: any;
  owner?: { id: Id; name: string } | null;
  country: any;
  state: any;
  gst_number: any;
}
interface QuotationRow {
  [k: string]: any;
}
interface ProductRow {
  [k: string]: any;
}

interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

interface ProductOption {
  value: number;
  label: string;
  data: {
    id: number;
    part_no: string;
    description: string;
    uom: any;
    specification: any;
    hsn_no: string;
    quantity: string;
    price: number;
    discount: string;
    net_price: number;
    igst_rate: string;
    principal: any;
  };
}

type EditorState = {
  index: number;
  field: "notes" | "description" | "product_specification" | "Comments";
} | null;

type FormError = {
  product_list?: { [index: number]: { [field: string]: string | undefined } };
} & { [key: string]: string | undefined };

/* ---------- Helpers ---------- */
const makeOptions = (
  list: Branch[] | CustomerDD[],
  labelKey = "name",
  extra: (i: any) => any = () => ({})
) =>
  (list as any[]).map((b: any) => ({
    value: b.id,
    label: b[labelKey],
    ...extra(b),
  }));

const numberOrZero = (v: any) => {
  if (v === "price") {
    return parseFloat(String(v));
  }

  const n = parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
};

const numberOrZeroStrict = (val: any) => {
  const str = val?.toString().trim();
  if (str === "") return "";
  if (/^\d*\.?\d*$/.test(str)) {
    return str;
  }
  return null;
};

const Quotation: React.FC = () => {
  const backendRoute = all_routes.backend;
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const showAlert = useCallback(
    (title: any) => {
      MySwal.fire({
        title,
        confirmButtonColor: "#2ecc71",
        confirmButtonText: "OK",
      });
    },
    [MySwal]
  );

  const getDefaultPaymentTerms = (paymentTerm = "") => `
<ol style="padding-left: 20px;">
  <li style="margin-bottom: 8px;">Quotation is Valid for 30 days.</li>
  <li style="margin-bottom: 8px;">
    Payment Terms: ${paymentTerm}
  </li>
  <li style="margin-bottom: 8px;">
    Any Government / Local Body Levies, taxes, Cess, Duties, Octroi
    will be extra <strong>"At Actuals"</strong>.
  </li>
  <li style="margin-bottom: 8px;">
    The Delivery Schedule mentioned against each item is as per the
    Quotation Date and is subject to prior sales.
  </li>
  <li style="margin-bottom: 8px;">
    <strong>Loading/Unloading:</strong> Material unloading on site will be in your scope.
  </li>
</ol>
`;

  /* ---------- State (existing) ---------- */
  const [list, setList] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({
    product_specification: getDefaultPaymentTerms(),
    payment_term_condition: getDefaultPaymentTerms(),
    date: dayjs().format("YYYY-MM-DD"),
    delivery_date_id: "",
    quotation_type_id: 1,
    group_id: null,
    company_id: null,
    owner_id: null,
    principal_id: null,
    billing_state_id: null,
    shipping_state_id: null,
    product_description: "",
    enq_ref: "",
    contact_person: "",
    prepard_by: "",
    currency_id: 1,
    delevery_date_id: null,
    payment_term_id: null,
    product_id: null,
    principal_type: "",
    company_name: "",
    state: "",
    gst_number: "",
    billing_address: "",
    billing_state: "",
    billing_city: "",
    billing_pin_code: "",
    billing_mobile: "",
    billing_email: "",
    billing_landline: "",
    shipping_address: "",
    shipping_state: "",
    shipping_city: "",
    shipping_pin_code: "",
    shipping_mobile: "",
    shipping_email: "",
    shipping_landline: "",
    total_amount: 0,
    courier_id: "",
    quotation_type: "CW",
    heading: "",
    branch_address: "",
    courier_name: "",
    shipping_state_name: "",
    show_note: true,
  });
  const DEFAULT_PAYMENT_TERMS = `
<ol style="padding-left: 20px;">
  <li style="margin-bottom: 8px;">Quotation is Valid for 30 days.</li>
  <li style="margin-bottom: 8px;">
    Payment Terms: ${
      formData.delivery_term_label || "Advance Against Proforma Invoice."
    }
  </li>
  <li style="margin-bottom: 8px;">
    Any Government / Local Body Levies, taxes, Cess, Duties, Octroi
    will be extra <strong>"At Actuals"</strong>.
  </li>
  <li style="margin-bottom: 8px;">
    The Delivery Schedule mentioned against each item is as per the
    Quotation Date and is subject to prior sales.
  </li>
  <li style="margin-bottom: 8px;">
    <strong>Loading/Unloading:</strong> Material unloading on site will be in your scope.
  </li>
</ol>
`;

  const [formDataPendingQuotation, setFormDataPendingQuotation] = useState<{
    current_follow_date: string;
    reason: any;
    reason_status_id: any;
  }>({
    current_follow_date: "",
    reason: "",
    reason_status_id: "",
  });

  const [formError, setFormError] = useState<FormError>({});
  const [formErrorPendingQuotation, setFormErrorPendingQuotation] = useState<{
    [k: string]: string;
  }>({});

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [query, setQuery] = useState("");
  // default date range = null (empty string)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      return 5;
    }
    return 10;
  });
  const [totalItems, setTotalItems] = useState(0);

  const [loading, setLoading] = useState(false);

  const [ownerFilter, setOwnerFilter] = useState<number[]>([]);
  const [branchFilter, setBranchFilter] = useState<number[]>([]);
  const [principalFilter, setPrincipalFilter] = useState<number[]>([]);
  const [currencyFilter, setCurrencyFilter] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<number[]>([]);

  const [companyList, setCompanyList] = useState<CustomerDD[]>([]);
  const [ownerList, setOwnerList] = useState<Branch[]>([]);
  const [quotationTypeList, setOuotationTypeList] = useState<Branch[]>([]);
  const [notificationList, setNotificationList] = useState<Branch[]>([]);
  const [addressInfo, setAddressInfo] = useState<any>({});
  const [currencyList, setCurrencyList] = useState<Branch[]>([]);
  const [branchList, setBranchList] = useState<Branch[]>([]);
  const [stateList, setStateList] = useState<Branch[]>([]);
  const [paymentAdvanceDate, setPaymentAdvanceDate] = useState<Branch[]>([]);
  const [statusList, setStatusList] = useState<Branch[]>([]);
  const [courierList, setCourierList] = useState<Branch[]>([]);
  const [principalList, setPrincipalList] = useState<Branch[]>([]);

  const [productRows, setProductRows] = useState<ProductRow[]>([]);
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null);
  const [quotationReason, setQuotationReason] = useState<any | null>(null);
  const [descReason, setDescReason] = useState<any | null>(null);

  const [orderTitle, setOrderTitle] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [currencyName, setCurrencyName] = useState("INR");

  const styleTagRef = useRef<HTMLStyleElement | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingSubmitting, setPendingSubmitting] = useState(false);
  const [orderPreviewTrue, setOrderPreviewTrue] = useState(false);

  /* ---------- Preview integration ---------- */
  const [showPreview, setShowPreview] = useState(false);
  const [showOrderPreview, setOrderShowPreview] = useState(false);

  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  /* ---------- Export states ---------- */
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [blinkField, setBlinkField] = useState<string | null>(null);
  const [quotationType, setQuotationType] = useState("CW");
  const [quotationFormat, setQuotationFormat] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [editorValue, setEditorValue] = useState("");
  const [editor, setEditor] = useState<EditorState>(null);
  const openEditor = (index: number, field: any) =>
    setEditor({
      index,
      field: field === "Comments" ? "product_specification" : field,
    });
  const closeEditor = () => setEditor(null);

  /* ---------- Mobile detection (used only to switch rendering) ---------- */
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  const blinkStyleFor = (field: string) =>
    blinkField === field
      ? {
          animation: "blinkHighlight 1s ease-in-out 2",
          borderRadius: "6px",
          padding: "6px",
          marginBottom: "0.25rem",
        }
      : { marginBottom: "0.25rem" };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (document.getElementById("blink-keyframes")) return;

    const style = document.createElement("style");
    style.id = "blink-keyframes";
    style.innerHTML = `
      @keyframes blinkHighlight {
        0% { background-color: rgba(220,53,69,0.15); }
        50% { background-color: rgba(220,53,69,0.35); }
        100% { background-color: transparent; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    setFormData((prev: any) => ({
      ...prev,
      payment_term_condition: getDefaultPaymentTerms(
        prev.delivery_date_custom || ""
      ),
    }));
  }, [formData.delivery_term_label]);
  /* ---------- Small helpers to keep validation errors in sync ---------- */
  const setField = useCallback((name: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    setFormError((prev) => {
      if (!prev) return prev;
      const u = { ...prev };
      delete (u as any)[name];
      return u;
    });
  }, []);

  const setPendingField = useCallback((name: string, value: any) => {
    setFormDataPendingQuotation((prev: any) => ({ ...prev, [name]: value }));
    setFormErrorPendingQuotation((prev) => {
      if (!prev) return prev;
      const u = { ...prev };
      delete (u as any)[name];
      return u;
    });
  }, []);

  const setRef = (name: string) => (el: HTMLDivElement | null) => {
    fieldRefs.current[name] = el;
  };

  /* ---------- Select options (memo) ---------- */
  const companyOptions = useMemo(
    () =>
      makeOptions(companyList, "company_name", (b: CustomerDD) => ({
        address: b.address,
        owner_name: b.owner_name,
        state_id: b.state_id,
        other_state: b.other_state,
        city: b.city,
        mobile_no: b.mobile_no,
        email_id: b.email_id,
        landline_no: b.landline_no,
        pin_code: b.pin_code,
        customer_name: b.customer_name,
        gst_number: b.gst_number,
        country: b.country?.name,
        state: b.state?.name,
      })),
    [companyList]
  );
  const ownerOptions = useMemo(() => makeOptions(ownerList), [ownerList]);
  const quotationTypeOptions = useMemo(
    () => makeOptions(quotationTypeList),
    [quotationTypeList]
  );
  const notificationOptions = useMemo(
    () => makeOptions(notificationList),
    [notificationList]
  );
  const stateOptions = useMemo(() => makeOptions(stateList), [stateList]);
  const currencyOptions = useMemo(
    () => makeOptions(currencyList),
    [currencyList]
  );
  const paymentAdvanceOptions = useMemo(
    () => makeOptions(paymentAdvanceDate),
    [paymentAdvanceDate]
  );
  const courierOptions = useMemo(() => makeOptions(courierList), [courierList]);
  const statusOptions = useMemo(() => makeOptions(statusList), [statusList]);
  const principalOptions = useMemo(
    () => makeOptions(principalList),
    [principalList]
  );

  const selectedOption = useCallback((opts: Option[], id?: Id | null) => {
    if (!id) return null;
    return opts.find((o) => o.value === id) || null;
  }, []);

  /* ---------- Totals (memo + effect) ---------- */
  const subTotal = useMemo(() => {
    return productRows.reduce((sum, r) => sum + numberOrZero(r.total), 0);
  }, [productRows]);

  useEffect(() => {
    setFormData((prev: any) =>
      prev.total_amount === subTotal
        ? prev
        : { ...prev, total_amount: subTotal }
    );
  }, [subTotal]);

  /* ---------- Data calls (unchanged) ---------- */
  const fetchList = useCallback(
    async ({ page = 1, perPage = 10, search = "" }) => {
      setLoading(true);
      if (search) {
        setLoadingSearch(true);
      }
      try {
        const result = await apiRequestCall("post", backendRoute.getQuotation, {
          data: {
            page,
            per_page: perPage,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            search: search,
            owner_list: ownerFilter,
            branch_list: branchFilter,
            principal_list: principalFilter,
            currency_list: currencyFilter,
            status_list: statusFilter,
          },
        });
        const res = result?.data?.data;
        if (res) {
          setList(res.data);
          setCurrentPage(res.current_page || page);
          setPerPage(res.per_page || perPage);
          setTotalItems(res.total || 0);
        } else {
          setList([]);
        }
      } catch (e) {
        console.error("Failed to fetch quotations:", e);
        setList([]);
      } finally {
        setLoading(false);
        setLoadingSearch(false);
      }
    },
    [
      backendRoute.getQuotation,
      endDate,
      startDate,
      ownerFilter,
      branchFilter,
      principalFilter,
      currencyFilter,
      statusFilter,
    ]
  );

  const getBranch = useCallback(async () => {
    const result = await apiRequestCall("post", backendRoute.getBranchDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setBranchList(
        Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }))
      );
    }
  }, [backendRoute.getBranchDD]);

  const getCompanyList = useCallback(async () => {
    const result = await apiRequestCall("post", backendRoute.getCompanyDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setCompanyList(
        raw.map((item: any) => ({
          id: item.id,
          company_name: item.company_name || "",
          gst_number: item.gst_number || "",
          owner_name: item?.owner?.name || "",
          address: item.address || "",
          state_id: item.state_id,
          other_state: item?.other_state,
          city: item?.city,
          country: item?.country,
          state: item?.state,
          email_id: item?.email_id,
          pin_code: item?.pin_code,
          mobile_no: item?.mobile_no,
          landline_no: item?.landline_no,
          customer_name: item?.customer_name,
        }))
      );
    }
  }, [backendRoute.getCompanyDD]);

  const getOwnerList = useCallback(async () => {
    const result = await apiRequestCall("post", backendRoute.getOwnerDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setOwnerList(
        Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }))
      );
    }
  }, [backendRoute.getOwnerDD]);

  const getPrincipalList = useCallback(async () => {
    const result = await apiRequestCall("post", backendRoute.getPrincipalDD);
    const raw = result?.data?.data;
    if (raw) {
      if (Array.isArray(raw)) {
        setPrincipalList(
          raw.map((item: any) => ({
            id: item.id,
            name: item.type || item.name,
          }))
        );
      } else if (typeof raw === "object") {
        setPrincipalList(
          Object.entries(raw).map(([id, name]) => ({
            id: +id,
            name: String(name),
          }))
        );
      }
    }
  }, [backendRoute.getPrincipalDD]);

  const getQuotationFormatList = useCallback(async () => {
    const result = await apiRequestCall(
      "post",
      backendRoute.getQuotationFormatDD
    );
    const raw = result?.data?.data;
    if (raw) {
      setQuotationFormat(raw?.branch_address);
    }
  }, [backendRoute.getQuotationFormatDD]);

  const getQuotationTypeList = useCallback(async () => {
    const result = await apiRequestCall(
      "post",
      backendRoute.getQuotationTypeDD
    );
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setOuotationTypeList(
        Object.entries(raw).map(([id, type]) => ({
          id: +id,
          name: String(type),
        }))
      );
    }
  }, [backendRoute.getQuotationTypeDD]);

  const getStateList = useCallback(async () => {
    const result = await apiRequestCall("post", backendRoute.getStateDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setStateList(
        Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }))
      );
    }
  }, [backendRoute.getStateDD]);

  const getNotificationList = useCallback(async () => {
    const result = await apiRequestCall("post", backendRoute.getNotificationDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setNotificationList(
        Object.entries(raw).map(([id, type]) => ({
          id: +id,
          name: String(type),
        }))
      );
    }
  }, [backendRoute.getNotificationDD]);

  const getCurrency = useCallback(async () => {
    const result = await apiRequestCall("post", backendRoute.getCurrencyDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setCurrencyList(
        Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }))
      );
    }
  }, [backendRoute.getCurrencyDD]);

  const getPaymentAdvanceList = useCallback(async () => {
    const result = await apiRequestCall(
      "post",
      backendRoute.getPaymentAdvanceDD
    );
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setPaymentAdvanceDate(
        Object.entries(raw).map(([id, date_type]) => ({
          id: +id,
          name: String(date_type),
        }))
      );
    }
  }, [backendRoute.getPaymentAdvanceDD]);

  const getStatusList = useCallback(async () => {
    const result = await apiRequestCall("post", backendRoute.getStatusDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setStatusList(
        Object.entries(raw).map(([id, type]) => ({
          id: +id,
          name: String(type),
        }))
      );
    }
  }, [backendRoute.getStatusDD]);

  const resolveDeliveryPeriod = (deliveryDateId: any) => {
    const deliveryPerodDays: Option[] = paymentAdvanceDate.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    return selectedOption(deliveryPerodDays, deliveryDateId)?.label || "";
  };

  const getCourierList = useCallback(async () => {
    const result = await apiRequestCall("post", backendRoute.getCourierDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setCourierList(
        Object.entries(raw).map(([id, type]) => ({
          id: +id,
          name: String(type),
        }))
      );
    }
  }, [backendRoute.getCourierDD]);

  const searchTextData = (text: string) => {
    setSearchText(text);
  };

  /* ---------- Effects (unchanged) ---------- */
  useEffect(() => {
    getCurrency();
    getCompanyList();
    getOwnerList();
    getQuotationTypeList();
    getQuotationFormatList();
    getNotificationList();
    getStateList();
    getBranch();
    getPaymentAdvanceList();
    getStatusList();
    getCourierList();
    getPrincipalList();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchList({
      page: 1,
      perPage,
      search: searchText,
    });
  }, [
    startDate,
    endDate,
    ownerFilter,
    branchFilter,
    principalFilter,
    currencyFilter,
    statusFilter,
    perPage,
    searchText,
  ]);

  const blinkStyle = {
    animation: "blinkHighlight 1s ease-in-out 2",
    borderRadius: "6px",
    transition: "background-color 0.3s ease",
  };

  useEffect(() => {
    fetchList({ page: currentPage, perPage, search:  searchText});
  }, [currentPage, perPage]);

  useEffect(() => {
    if (!showForm) return;
    if (!styleTagRef.current) {
      const tag = document.createElement("style");
      tag.innerHTML = `
      
/* ---------- THEME VARIABLES ---------- */
:root {
  --small-color: #495057;
  --card-bg: #ffffff;
  --card-border: rgba(33, 37, 41, 0.06);
  --card-text: #111827;
  --input-border: rgba(0, 0, 0, 0.08);
  --accent: #0d6efd;
}

/* Auto dark mode */
@media (prefers-color-scheme: dark) {
  :root {
    --small-color: #ffffff;
    --card-bg: #0f0f12;
    --card-border: rgba(255, 255, 255, 0.04);
    --card-text: #e6eef6;
    --input-border: rgba(255, 255, 255, 0.06);
    --accent: #78a9ff;
  }
}

/* Manual dark override */
.dark-mode,
[data-theme="dark"] {
  --small-color: #ffffff;
  --card-bg: #0f0f12;
  --card-border: rgba(255, 255, 255, 0.04);
  --card-text: #e6eef6;
  --input-border: rgba(255, 255, 255, 0.06);
  --accent: #78a9ff;
}

/* ---------- OFFCANVAS ---------- */
.offcanvas-end.offcanvas-large {
  width: 1600px !important;
  visibility: visible !important;
}

/* ---------- TYPOGRAPHY ---------- */
.small,
small {
  font-size: 0.875em;
  color: var(--small-color);
}

/* ---------- MOBILE ONLY ---------- */
@media (max-width: 768px) {
  .offcanvas.offcanvas-end.offcanvas-large.show {
    width: 100% !important;
    left: 0 !important;
    transform: none !important;
    padding: 12px;
  }

  .offcanvas-body .row > .col-6 {
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100% !important;
  }

  .quill-mobile .ql-editor {
    min-height: 120px;
    max-height: none !important;
    overflow: visible !important;
  }

  .table-responsive.no-scrollbar {
    overflow-x: visible !important;
  }

  .quotation-product-card {
    width: 100%;
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    padding: 12px;
    border-radius: 10px;
    margin-bottom: 12px;
    color: var(--card-text);
  }

  .quotation-product-card .field-label {
    font-size: 12px;
    color: var(--small-color);
    margin-bottom: 4px;
  }

  .quotation-product-card .field-value {
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .quotation-product-card .form-control,
  .quotation-product-card button {
    font-size: 14px;
    padding: 8px 10px;
    background: var(--card-bg);
    color: var(--card-text);
    border: 1px solid var(--input-border);
    border-radius: 6px;
  }

  .quotation-product-card .d-flex {
    gap: 10px;
  }

  .part-no-badge {
    font-weight: 700;
    display: block;
    background: linear-gradient(
      90deg,
      rgba(13, 110, 253, 0.06),
      rgba(13, 110, 253, 0.02)
    );
    color: var(--accent);
    padding: 6px 8px;
    border-radius: 6px;
  }

  textarea.form-control {
    max-height: 160px;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }

  .quotation-product-card textarea.form-control::-webkit-scrollbar {
    width: 8px;
  }

  .quotation-product-card textarea.form-control::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.08);
    border-radius: 6px;
  }

  .card-field-grid {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .card-field {
    flex: 1 1 120px;
    min-width: 120px;
  }
}

/* Mobile dark scrollbar */
@media (max-width: 768px) and (prefers-color-scheme: dark) {
  .quotation-product-card textarea.form-control::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.06);
  }
}

/* ---------- CLOSE BUTTON ---------- */
.btn-close {
  filter: none;
}

@media (prefers-color-scheme: dark) {
  .btn-close {
    filter: invert(1) grayscale(100%) brightness(200%);
  }
}
    `;

      document.head.appendChild(tag);
      styleTagRef.current = tag;
    }
    return () => {
      if (styleTagRef.current) {
        styleTagRef.current.remove();
        styleTagRef.current = null;
      }
    };
  }, [showForm]);

  /* ---------- Handlers (unchanged) ---------- */
  const handleSelectChange = useCallback((name: string, selected: any) => {
    setFormError((prev) => {
      const u = { ...prev };
      delete (u as any)[name];
      return u;
    });

    if (name === "quotation_type_id") {
      setQuotationType(selected?.label ?? null);

      setFormData((prev: any) => ({
        ...prev,
        quotation_type: selected?.label ?? null,
      }));
    }

    if (name === "courier_id") {
      setFormData((prev: any) => ({
        ...prev,
        courier_name: selected?.label ?? null,
      }));
    }

    if (name === "shipping_state_id") {
      setFormData((prev: any) => ({
        ...prev,
        shipping_state_name: selected?.label ?? null,
      }));
    }

    if (name === "currency_id") {
      setCurrencyName(selected?.label || "");
      setFormData((prev: any) => ({
        ...prev,
        ["currency"]: selected?.label ?? null,
      }));
    }

    if (name === "company_id") {
      const updates: any = {
        company_id: selected?.value ?? null,
        company_name: selected?.label || null,
        gst_number: selected?.gst_number || null,
        state: selected?.state,
        country: selected?.country,
        billing_address: selected?.address || null,
        billing_city: selected?.city || null,
        billing_state_id: selected?.state_id || "",
        billing_mobile: selected?.mobile_no || "",
        billing_email: selected?.email_id || "",
        billing_landline: selected?.landline_no || "",
        billing_pin_code: selected?.pin_code || "",
        contact_person: selected?.customer_name || "",
        shipping_address: selected?.address || null,
        shipping_city: selected?.city || null,
        shipping_state_id: selected?.state_id || "",
      };

      if (selected?.other_state != null && selected?.other_state != "") {
        updates.billing_other_state = selected.other_state;
        updates.state = selected.other_state;
      }

      setFormError((prev) => {
        const u = { ...prev };
        Object.keys(updates).forEach((k) => delete (u as any)[k]);
        return u;
      });

      setFormData((prev: any) => ({ ...prev, ...updates }));
      setAddressInfo(selected || {});
      return;
    }

    if (name === "billing_state_id") {
      setFormData((prev: any) => ({
        ...prev,
        state: selected?.label,
        billing_state_id: selected?.value ?? null,
      }));
      return;
    }

    setFormData((prev: any) => ({
      ...prev,
      [name]: selected?.value ?? null,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      lead_from: "",
      product_specification: DEFAULT_PAYMENT_TERMS,
      payment_term_condition: DEFAULT_PAYMENT_TERMS,
      product_id: "",
      principal_id: "",
      product_description: "",
      principal_type: "",
      total_amount: 0,
      date: dayjs().format("YYYY-MM-DD"),
      delivery_date_id: "",
      quotation_type_id: 1,
      quotation_type: "CW",
      shipping_state_name: "",
      group_id: null,
      company_id: null,
      owner_id: null,
      billing_state_id: null,
      shipping_state_id: null,
      enq_ref: "",
      contact_person: "",
      prepard_by: "",
      currency_id: 1,
      delevery_date_id: null,
      payment_term_id: null,
      billing_address: "",
      billing_state: "",
      billing_city: "",
      billing_pin_code: "",
      billing_mobile: "",
      billing_email: "",
      billing_landline: "",
      shipping_address: "",
      shipping_state: "",
      shipping_city: "",
      shipping_pin_code: "",
      shipping_mobile: "",
      shipping_email: "",
      shipping_landline: "",
      overdue_no: "",
      overdues_value: "",
      courier_id: "",
      company_name: "",
      state: "",
      country: "",
      branch_address: "",
      show_note: true,
    });
    setCurrencyName("INR");
    setFormError({});
    setSelectedId(null);
    setShowForm(false);
    setProductRows([]);
    setQuotationType("CW");
  }, []);

  const updateRecord = useCallback(
    (row: any) => {
      if (row?.order) {
        const selectedCurrency = currencyList.find(
          (c) => c.id === row.currency_id
        );
        setCurrencyName(selectedCurrency?.name || "");
        setFormData({ ...row, ["currency_id"]: row?.currency_id });
        setOrderTitle(row?.order);
      }

      setSelectedId(row.id);
      setShowForm(true);
      setOrderPreviewTrue(false);
      const quotationTypeOptions: Option[] = quotationTypeList.map((item) => ({
        value: item.id,
        label: item.name,
      }));
      const quotationType =
        selectedOption(quotationTypeOptions, row?.quotation_type_id)?.label ??
        "CW";

      const shippingStateName = selectedOption(
        stateOptions,
        row?.shipping_state_id
      )?.label;

      const deliveryPerodDays: Option[] = paymentAdvanceDate.map((item) => ({
        value: item.id,
        label: item.name,
      }));

      const deliveryPerod = selectedOption(
        deliveryPerodDays,
        row?.delivery_date_id
      )?.label;

      setFormData({
        ...row,
        contact_person: row?.billing_contact_person,
        quotation_type: quotationType,
        shipping_state: shippingStateName,
        shipping_state_name: shippingStateName,
        delivery_date_custom: deliveryPerod,
      });

      setProductRows(row?.quotation_details || []);
    },
    [currencyList, formData.currency_id]
  );

  const deleteRecord = useCallback((id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteId) return;
    const result = await apiRequestCall("post", backendRoute.deleteQuotation, {
      data: { id: deleteId },
    });
    if (result?.data?.code === 200) {
      fetchList({ page: currentPage, perPage, search:searchText});
      setShowMessage(result?.data?.message);
      setShowSuccess(true);
    }
    setDeleteId(null);
    setShowDeleteModal(false);
  }, [backendRoute.deleteQuotation, currentPage, deleteId, fetchList, perPage]);

  const loadProductOptions = useCallback(
    async (inputValue: string): Promise<ProductOption[]> => {
      if (!inputValue.trim()) return [];
      const response = await apiRequestCall("post", backendRoute.getProductDD, {
        data: { search: inputValue },
      });
      const raw = response?.data?.data || [];
      return raw.map((item: any) => ({
        value: item.id,
        label: item.part_no,
        data: item,
      }));
    },
    [backendRoute.getProductDD]
  );

  const handleSelectChangeFilter = useCallback(
    (_field: string, selected: SingleValue<ProductOption>) => {
      if (!selected) return;
      const product = (selected as ProductOption).data;
      const index = productRows.findIndex(
        (row) =>
          String(row.part_no || "")
            .trim()
            .toLowerCase() ===
          String(product.part_no || "")
            .trim()
            .toLowerCase()
      );

      // if (index !== -1) {
      //   showAlert(
      //     `<h6>Part No. "${product.part_no}" is already added. Manage existing entry!</h6>`
      //   );
      //   setHighlightIndex(index);
      //   setTimeout(() => setHighlightIndex(null), 3000);
      //   return;
      // }

      const lastRow =
        productRows.length > 0 ? productRows[productRows.length - 1] : null;

      const newRow: ProductRow = {
        principal_id: product?.principal?.id,
        product_id: product?.id || "",
        part_no: product.part_no || "",
        description: product.description || "",

        principal: product?.principal || "",
        uom: product?.uom || "",
        specification: product?.specification || "",

        hsn_code: product.hsn_no || "",
        quantity: lastRow ? lastRow.quantity : 1,
        in_stock: product.quantity,
        price: product.price || "",
        discount:
          product.discount !== null &&
          product.discount !== undefined &&
          product.discount !== ""
            ? Number(product.discount).toFixed(2)
            : "0.00",
        net_price: product.net_price || "",
        igst: product.igst_rate || "",
        total: product.net_price || "",
        notes: "",
      };

      setProductRows((prev) => {
        const updated = [...prev, newRow].map((row) => {
          const price = numberOrZeroStrict(row.price);
          const qty = numberOrZero(row.quantity);
          const discount = numberOrZero(row.discount);
          const igst = numberOrZero(row.igst);

          const discountedPrice = price - (price * discount) / 100;
          const netPrice = discountedPrice * qty;
          const totalWithTax = netPrice + (netPrice * igst) / 100;

          return {
            ...row,
            net_price: +netPrice.toFixed(2),
            total: +totalWithTax.toFixed(2),
          };
        });
        return updated;
      });
    },
    [productRows, showAlert]
  );

  const deleteProduct = useCallback((index: number) => {
    setProductRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const toggleDescriptionEditor = useCallback((index: number) => {
    setProductRows((prev) =>
      prev.map((row, i) =>
        i === index
          ? { ...row, showDescriptionEditor: !row.showDescriptionEditor }
          : row
      )
    );
  }, []);

  const updateRow = useCallback((index: number, field: string, value: any) => {
    setProductRows((prev) => {
      const updated = [...prev];

      // quantity = integer
      const numericFields = ["quantity"];

      // allow decimals
      const decimalFields = ["price", "discount", "igst"];

      const parsedValue = numericFields.includes(field)
        ? numberOrZero(value)
        : decimalFields.includes(field)
        ? numberOrZeroStrict(value)
        : value;

      // validate decimal fields
      if (
        decimalFields.includes(field) &&
        value !== "" &&
        !/^\d*\.?\d*$/.test(String(value))
      ) {
        return prev;
      }

      updated[index] = {
        ...updated[index],
        ...(field === "principal"
          ? {
              principal: {
                id: updated[index].principal?.id ?? null,
                type: parsedValue,
              },
            }
          : { [field]: parsedValue }),
      };

      const quantity = numberOrZero(updated[index].quantity);
      const price = Number(updated[index].price || 0);
      const discount = Number(updated[index].discount || 0);
      const igstRate = Number(updated[index].igst || 0);

      const subtotal = quantity * price;
      const discountAmount = subtotal * (discount / 100);
      const discountedTotal = subtotal - discountAmount;
      const igstAmount = discountedTotal * (igstRate / 100);

      updated[index].net_price = Number(discountedTotal.toFixed(2));
      updated[index].total = Number((discountedTotal + igstAmount).toFixed(2));

      return updated;
    });

    setFormError((prev) => {
      if (!prev.product_list?.[index]) return prev;

      const next = { ...prev };
      const rowErrors = { ...next.product_list![index] };

      delete rowErrors[field];

      if (Object.keys(rowErrors).length === 0) {
        const newPL = { ...next.product_list };
        delete (newPL as any)[index];

        if (Object.keys(newPL).length === 0) {
          delete (next as any).product_list;
        } else {
          next.product_list = newPL;
        }
      } else {
        next.product_list = {
          ...next.product_list,
          [index]: rowErrors,
        };
      }

      return next;
    });
  }, []);

  const handleRow = useCallback((row: any) => {
    setFormDataPendingQuotation({
      current_follow_date: "",
      reason: "",
      reason_status_id: row?.pending_quotation_details?.reason_status_id,
    });
    setFormErrorPendingQuotation({});
    const bulletReason =
      row?.pending_quotation_details?.reason
        ?.split(",")
        .map((i: any) => String(i).trim())
        .filter(Boolean)
        .map((i: any) => `• ${i}`)
        .join("\n") || "";
    setDescReason(bulletReason);
    setQuotationReason(row);
    setShowMessageModal(true);
  }, []);

  const updateReasonStatus = useCallback(async () => {
    try {
      setPendingSubmitting(true);
      const result = await apiRequestCall(
        "post",
        backendRoute.updateQuotationStatus,
        {
          data: {
            current_follow_date:
              formDataPendingQuotation?.current_follow_date == ""
                ? dayjs().format("YYYY-MM-DD")
                : formDataPendingQuotation?.current_follow_date,
            reason: formDataPendingQuotation?.reason,
            reason_status_id: formDataPendingQuotation?.reason_status_id,
            unique_quotation_no: quotationReason?.unique_quotation_no,
            quotation_id: quotationReason?.id,
          },
        }
      );

      if (result?.data?.code === 221) {
        setFormErrorPendingQuotation(
          parseValidationErrors(result?.data?.errors)
        );
        return;
      }

      if (result?.data?.code === 200) {
        fetchList({ page: currentPage, perPage, search:searchText });
        setShowMessage(result?.data?.message);
        setShowSuccess(true);
        setShowMessageModal(false);
      } else {
        setShowMessage(result?.data?.message || "Failed to update status");
      }

      setDeleteId(null);
    } catch (err) {
      console.error("Failed to update reason status:", err);
      setShowMessage("Something went wrong. Please try again.");
      setShowMessageModal(false);
    } finally {
      setPendingSubmitting(false);
    }
  }, [
    apiRequestCall,
    backendRoute.updateQuotationStatus,
    formDataPendingQuotation,
    quotationReason,
    fetchList,
    currentPage,
    perPage,
    parseValidationErrors,
    setFormErrorPendingQuotation,
    setShowMessage,
    setShowSuccess,
    setDeleteId,
    setPendingSubmitting,
  ]);

  const handleSubmit = useCallback(
    async (quotation_type: any) => {
      try {
        setSubmitting(true);

        const payload = {
          ...formData,
          product_list: productRows,
          update_status: !!selectedId,
          quotation_id: selectedId,
          submit_type: quotation_type,
        };

        const route = orderTitle
          ? backendRoute.storeOrder
          : backendRoute.addUpdateQuotation;
        const result = await apiRequestCall("post", route, { data: payload });

        if (result?.data?.code === 221) {
          setFormError(parseValidationErrors(result?.data?.errors));
          return;
        }

        if (result?.data?.message == "quotation_preview") {
          setShowPreview(true);
          return;
        }

        if (result?.data?.message == "order_preview") {
          setOrderShowPreview(true);
          return;
        }
        if (result?.data?.code === 200) {
          setShowPreview(false);
          setOrderShowPreview(false);
          resetForm();
          setCurrencyName("INR");
          setShowSuccess(true);
          setShowMessage(result?.data?.message);
          if (orderTitle) {
            navigate("/order");
            return;
          }
          fetchList({ page: currentPage, perPage, search:  searchText });
        }
      } catch (error) {
        console.error("Failed to submit:", error);
        setList([]);
      } finally {
        setSubmitting(false);
      }
    },
    [
      backendRoute.addUpdateQuotation,
      backendRoute.storeOrder,
      currentPage,
      fetchList,
      formData,
      orderTitle,
      perPage,
      productRows,
      resetForm,
      selectedId,
      apiRequestCall,
      parseValidationErrors,
      setFormError,
      setCurrencyName,
      setShowSuccess,
      setShowMessage,
      setList,
      setSubmitting,
    ]
  );

  useEffect(() => {
    const firstErrorKey = Object.keys(formError)[0];

    if (firstErrorKey && fieldRefs.current[firstErrorKey]) {
      const el = fieldRefs.current[firstErrorKey];

      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      setBlinkField(firstErrorKey);

      setTimeout(() => setBlinkField(null), 1000000);
    }
  }, [formError]);

  /* ---------- Derived ---------- */
  const isStatusLocked = ["closed", "lose", "win"].includes(
    quotationReason?.pending_quotation_details?.status_code
  );

  const saveEditorValue = () => {
    if (!editor) return;

    const field =
      editor.field === "Comments" ? "product_specification" : editor.field;

    setProductRows((prev) => {
      const updated = [...prev];

      updated[editor.index] = {
        ...updated[editor.index],
        [field]: editorValue,
      };

      return updated;
    });

    showAlert(`<h6>Saved Successfully ! <h6>`);
    closeEditor();
  };

  useEffect(() => {
    if (!editor) return;

    const field =
      editor.field === "Comments" ? "product_specification" : editor.field;

    setEditorValue(productRows[editor.index]?.[field] || "");
  }, [editor, productRows]);

  /* ---------- Render helpers for mobile product layout ---------- */
  const renderMobileProductCards = (): JSX.Element[] =>
    productRows.map((row: any, index: number) => {
      // theme detection (safe for SSR)
      const prefersDark =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches;

      const theme = {
        cardBg: prefersDark ? "#0b1220" : "#ffffff",
        cardText: prefersDark ? "#e6eef6" : "#111827",
        muted: prefersDark ? "#98a2ad" : "#6c757d",
        inputBg: prefersDark ? "#0f1724" : "#ffffff",
        inputBorder: prefersDark ? "rgba(255,255,255,0.06)" : "#e9ecef",
        accent: prefersDark ? "#78a9ff" : "#0d6efd",
        btnBorder: prefersDark
          ? "rgba(255,255,255,0.06)"
          : "rgba(13,110,253,0.12)",
      };

      const styles: { [k: string]: React.CSSProperties } = {
        card: {
          background: theme.cardBg,
          color: theme.cardText,
          border: `1px solid ${theme.inputBorder}`,
          borderRadius: 10,
          padding: 12,
          marginBottom: 12,
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          maxWidth: 640,
        },
        partRow: {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        },
        partBadge: {
          fontWeight: 700,
          color: theme.accent,
          background: prefersDark
            ? "rgba(120,169,255,0.06)"
            : "rgba(13,110,253,0.04)",
          padding: "6px 10px",
          borderRadius: 6,
          maxWidth: "100%",
          overflowWrap: "break-word",
          wordBreak: "break-word",
        },
        label: { fontSize: 12, color: theme.muted, marginBottom: 4 },
        textarea: {
          background: theme.inputBg,
          color: theme.cardText,
          border: `1px solid ${theme.inputBorder}`,
          borderRadius: 6,
          padding: "0.5rem",
          maxHeight: 140,
          minHeight: 72,
          resize: "vertical",
          width: "100%",
          fontSize: 14,
        },
        grid: {
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 10,
          alignItems: "start",
        },
        field: { display: "flex", flexDirection: "column", gap: 6 },
        fieldValue: {
          fontSize: 14,
          color: theme.cardText,
          wordBreak: "break-word",
        },
        qtyControl: { display: "flex", gap: 8, alignItems: "center" },
        qtyBtn: {
          minWidth: 36,
          height: 36,
          padding: 0,
          borderRadius: 6,
          border: `1px solid ${theme.btnBorder}`,
          background: "transparent",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.cardText,
        },
        qtyInput: {
          width: 56,
          textAlign: "center",
          height: 36,
          padding: "4px 8px",
          borderRadius: 6,
          border: `1px solid ${theme.inputBorder}`,
          background: theme.inputBg,
          color: theme.cardText,
        },
        input: {
          background: theme.inputBg,
          color: theme.cardText,
          border: `1px solid ${theme.inputBorder}`,
          borderRadius: 6,
          height: 36,
          padding: "6px 8px",
        },
        actionsWrap: { display: "flex", gap: 8, justifyContent: "flex-end" },
        actionBtn: {
          minWidth: 44,
          height: 36,
          padding: 0,
          borderRadius: 8,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${theme.btnBorder}`,
          background: "transparent",
          color: theme.cardText,
        },
        totalWrap: {
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          fontWeight: 700,
          alignItems: "center",
          color: theme.cardText,
          maxWidth: 180,
        },
      };

      return (
        <div
          key={index}
          className="quotation-product-card shadow-sm rounded-3 p-3 mb-2"
          style={{
            background: "#0b1622",
            color: "#fff",
            border: "1px solid #1e2b3c",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "140px 1fr",
              rowGap: 8,
            }}
          >
            <div style={{ fontWeight: 600, opacity: 0.9 }}>Part No.</div>
            <div style={{ fontWeight: 500 }}>{row.part_no || "-"}</div>

            <div style={{ fontWeight: 600, opacity: 0.9 }}>In Stock</div>
            <div>{row.in_stock ?? "-"}</div>

            <div style={{ fontWeight: 600, opacity: 0.9 }}>HSN</div>
            <div>{row.hsn_code || "-"}</div>

            <div style={{ fontWeight: 600, opacity: 0.9 }}>Description</div>
            <textarea
              className="form-control form-control-sm"
              rows={2}
              value={row.description || ""}
              onChange={(e) => updateRow(index, "description", e.target.value)}
              style={{
                background: "#101b2c",
                color: "#fff",
                border: "1px solid #2a3c54",
                resize: "none",
                height: "100px",
              }}
            />

            <div style={{ fontWeight: 600, opacity: 0.9 }}>Quantity</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={() =>
                  updateRow(
                    index,
                    "quantity",
                    Math.max(0, numberOrZero(row.quantity) - 1)
                  )
                }
              >
                −
              </button>
              <input
                className="form-control form-control-sm text-center"
                style={{
                  width: 60,
                  background: "#101b2c",
                  color: "#fff",
                  border: "1px solid #2a3c54",
                }}
                value={row.quantity ?? 0}
                onChange={(e) =>
                  updateRow(index, "quantity", numberOrZero(e.target.value))
                }
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={() =>
                  updateRow(index, "quantity", numberOrZero(row.quantity) + 1)
                }
              >
                +
              </button>
            </div>

            <div style={{ fontWeight: 600, opacity: 0.9 }}>
              Price [{currencyName}]
            </div>
            <input
              className="form-control form-control-sm"
              value={row.price ?? ""}
              onChange={(e) => updateRow(index, "price", e.target.value)}
              style={{
                background: "#101b2c",
                color: "#fff",
                border: "1px solid #2a3c54",
              }}
            />

            <div style={{ fontWeight: 600, opacity: 0.9 }}>Disc %</div>
            <input
              className="form-control form-control-sm"
              value={row.discount ?? ""}
              onChange={(e) => updateRow(index, "discount", e.target.value)}
              style={{
                background: "#101b2c",
                color: "#fff",
                border: "1px solid #2a3c54",
              }}
            />

            <div style={{ fontWeight: 600, opacity: 0.9 }}>IGST %</div>
            <input
              className="form-control form-control-sm"
              value={row.igst ?? ""}
              onChange={(e) => updateRow(index, "igst", e.target.value)}
              style={{
                background: "#101b2c",
                color: "#fff",
                border: "1px solid #2a3c54",
              }}
            />

            <div style={{ fontWeight: 600, opacity: 0.9 }}>Total</div>
            <div>{Number(row.total || 0).toLocaleString()}</div>
          </div>

          {/* Divider */}
          <hr style={{ borderColor: "#1e2b3c", margin: "10px 0" }} />

          {/* Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-info"
                onClick={() => toggleDescriptionEditor(index)}
                title="Add Product Description"
              >
                <i className="feather-book" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => deleteProduct(index)}
                disabled={productRows.length === 1}
                title="Delete"
              >
                <i className="feather-trash" />
              </button>
            </div>
            <div style={{ fontWeight: 600 }}>
              Total:{" "}
              <span style={{ color: "#00ff99" }}>
                {Number(row.total || 0).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Collapsible Description */}
          {row.showDescriptionEditor && (
            <div style={{ marginTop: 10 }}>
              <QuillEditor
                value={row.product_specification}
                onChange={(html: any) => {
                  setProductRows((prev: any[]) => {
                    const updated = [...prev];
                    updated[index].product_specification = html;
                    return updated;
                  });
                }}
                placeholder="Write product specification here..."
                height={140}
              />
            </div>
          )}
        </div>
      );
    });

  /* ---------- Render ---------- */
  const [activeTooltipRow, setActiveTooltipRow] = useState<number | null>(null);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  const columns = [
    {
      key: "id",
      label: "Sr",
      render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
    },
    { key: "company_details.company_name", label: "Company Name" },
    { key: "unique_quotation_no", label: "Quotation No" },
    { key: "total_amount", label: "Total" },
    { key: "lead_from", label: "Lead From" },
    { key: "branch_details.name", label: "Branch" },
    {
      key: "created_at",
      label: "Created Date",
      render: (row: any) =>
        row.created_at
          ? dayjs(row.created_at, "DD-MM-YYYY").format("DD/MM/YYYY")
          : "",
    },

    ...(permissionMap?.quotation_detail?.view_quotation_detail_reason ||
    permissionMap?.quotation_detail?.edit_quotation_detail_reason
      ? [
          {
            key: "reason",
            label: "Reasons",
            render: (row: any) => (
              <div className="hstack gap-2 fs-15">
                {permissionMap?.quotation_detail
                  ?.view_quotation_detail_reason && (
                  <button
                    title="View Reason"
                    type="button"
                    className="btn btn-soft-success btn-sm"
                    onClick={() => handleRow(row)}
                  >
                    <i className="ti ti-file" />
                  </button>
                )}
              </div>
            ),
          },
        ]
      : []),
    ...(permissionMap?.quotation_detail?.generate_quotation_detail_order ||
    permissionMap?.quotation_detail?.download_quotation_detail
      ? [
          {
            key: "operations",
            label: "Operations",
            render: (row: QuotationRow) => (
              <div className="hstack gap-2 fs-15 align-items-center">
                {/* STATUS BADGES – always allowed */}
                {row?.pending_quotation_details?.status_code === "open" && (
                  <span className="badge rounded-pill badge-soft-success">
                    Open
                  </span>
                )}

                {row?.pending_quotation_details?.status_code === "win" && (
                  <span className="badge rounded-pill badge-soft-info">
                    Win
                  </span>
                )}

                {row?.pending_quotation_details?.status_code === "closed" && (
                  <span className="badge rounded-pill badge-soft-warning">
                    Close
                  </span>
                )}

                {row?.pending_quotation_details?.status_code === "lose" && (
                  <span className="badge rounded-pill badge-soft-danger">
                    Lose
                  </span>
                )}

                {/* GENERATE ORDER – permission bound */}
                {permissionMap?.quotation_detail
                  ?.generate_quotation_detail_order &&
                  (row?.pending_quotation_details?.status_code != "win" ? (
                    <button
                      className="btn btn-icon btn-sm btn-soft"
                      style={{ backgroundColor: "rgb(213.4, 229.6, 251.4)" }}
                      onClick={() => {
                        updateRecord({ ...row, order: true });
                        setOrderPreviewTrue(true);
                      }}
                    >
                      <i className="feather feather-shopping-cart" />
                    </button>
                  ) : (
                    <button
                      className="btn btn-icon btn-sm btn-soft"
                      style={{ backgroundColor: "#ffc107" }}
                    >
                      <i className="feather feather-package" />
                    </button>
                  ))}

                {/* DOWNLOAD PDF – permission bound */}
                {permissionMap?.quotation_detail?.download_quotation_detail && (
                  <div style={{ position: "relative" }}>
                    <button
                      className="btn btn-icon btn-sm btn-soft-warning"
                      onClick={() => handlePdfDownload(row)}
                    >
                      <i className="feather feather-download" />
                    </button>

                    {activeTooltipRow === row.id && (
                      <div
                        style={{
                          position: "absolute",
                          top: "-35px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          background: "#212529",
                          color: "#fff",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          zIndex: 9999,
                        }}
                      >
                        {row?.pdf_status == "processing"
                          ? "PDF is being processed..."
                          : row?.pdf_status == "failed"
                          ? "PDF generation failed"
                          : "Request failed !. Please try again later."}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ),
          },
        ]
      : []),
    ...(permissionMap?.quotation_detail?.edit_quotation_detail ||
    permissionMap?.quotation_detail?.delete_quotation_detail
      ? [
          {
            key: "action",
            label: "Action",
            render: (row: QuotationRow) => (
              <div className="hstack gap-2 fs-15">
                {/* EDIT QUOTATION */}
                {permissionMap?.quotation_detail?.edit_quotation_detail && (
                  <button
                    title="Update quotation"
                    className="btn btn-icon btn-sm btn-soft-info"
                    onClick={() => {
                      updateRecord(row);
                      setFormError({});
                      setOrderTitle(false);

                      const rec = selectedOption(
                        quotationTypeOptions,
                        row?.quotation_type_id
                      );

                      setQuotationType(rec?.label || "CW");
                    }}
                  >
                    <i
                      className={
                        row?.is_order_pending === "1"
                          ? "feather-edit"
                          : "feather-eye"
                      }
                    />
                  </button>
                )}

                {/* DELETE QUOTATION */}
                {permissionMap?.quotation_detail?.delete_quotation_detail && (
                  <button
                    title="Delete quotation"
                    disabled={["lose", "closed", "win"].includes(
                      row?.pending_quotation_details?.status_code
                    )}
                    className="btn btn-icon btn-sm btn-soft-danger"
                    onClick={() => deleteRecord(Number(row.id))}
                  >
                    <i className="feather-trash" />
                  </button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  const handlePdfDownload = async (row: QuotationRow) => {
    try {
      const payload = {
        quotation_id: row.id,
        unique_quotation_no: row.unique_quotation_no,
      };

      const result = await apiRequestCall(
        "post",
        backendRoute.quotationPdfStatus,
        { data: payload }
      );

      if (result?.data?.data?.status === "ready") {
        const path = row.base_pdf_url + "/" + row.pdf_name;
        window.open(path, "_blank");
        return;
      }

      setActiveTooltipRow(row.id);

      setTimeout(() => {
        setActiveTooltipRow(null);
      }, 1000);
    } catch {
      setActiveTooltipRow(row.id);

      setTimeout(() => {
        setActiveTooltipRow(null);
      }, 1000);
    }
  };

  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title mb-0">
                  Quotation
                  <span className="count-title">{totalItems}</span>
                </h4>
              </div>
              <div className="col-12 col-md-4">
                <div className="card-control position-relative">
                  <div className="icon-form">
                    <Search
                      placeholder="Enter Text ..."
                      value={query}
                      onChange={setQuery}
                      searchText={searchTextData}
                      loading={loadingSearch}
                    />
                  </div>
                </div>
              </div>

              {permissionMap?.quotation_detail?.add_quotation_detail && (
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      className="btn btn-soft-success d-flex align-items-center"
                      onClick={() => {
                        resetForm();
                        setShowForm(true);
                        setSameAsBilling(false);
                        setOrderPreviewTrue(false);
                      }}
                      disabled={submitting}
                      aria-label="Add new owner"
                    >
                      <i className="ti ti-square-rounded-plus" />
                      <span className="d-none d-md-inline ms-1">New</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-2">
                <div className="card-control position-relative">
                  <div className="icon-form">
                    <span className="form-icon">
                      <i className="ti ti-calendar" />
                    </span>
                    <DateRangePicker
                      initialSettings={{
                        autoUpdateInput: false,
                        locale: { cancelLabel: "Clear" },
                      }}
                      onCallback={(start, end) => {
                        setStartDate(start.format("YYYY-MM-DD"));
                        setEndDate(end.format("YYYY-MM-DD"));
                      }}
                      onCancel={() => {
                        setStartDate("");
                        setEndDate("");
                      }}
                    >
                      <input
                        type="text"
                        className="form-control bookingrange"
                        placeholder="Select Date Range Filter"
                        value={
                          startDate && endDate
                            ? `${dayjs(startDate).format(
                                "DD/MM/YYYY"
                              )} - ${dayjs(endDate).format("DD-MM-YYYY")}`
                            : ""
                        }
                        readOnly
                        style={{ cursor: "pointer" }}
                        aria-label="Select date range"
                      />
                    </DateRangePicker>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Owner"
                  endpoint="getOwnerDD"
                  selected={ownerFilter}
                  onChange={(v) => setOwnerFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Branch"
                  endpoint="/getBranchDD"
                  selected={branchFilter}
                  onChange={(v) => setBranchFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Currency"
                  endpoint="/getCurrencyDD"
                  selected={currencyFilter}
                  onChange={(v) => setCurrencyFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Principal"
                  endpoint="/getPrincipalDD"
                  selected={principalFilter}
                  onChange={(v) => setPrincipalFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Status"
                  endpoint="getStatusDD"
                  selected={statusFilter}
                  onChange={(v) => setStatusFilter(v as number[])}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            {loading ? (
              <Loader />
            ) : (
              <div className="table-responsive">
                <Table data={list} columns={columns} />
              </div>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onPerPageChange={(n: number) => {
              setPerPage(n);
              setCurrentPage(1);
            }}
            perPageOptions={[
              { value: 5, label: "5" },
              { value: 10, label: "10" },
              { value: 25, label: "25" },
            ]}
          />
        </div>

        {/* Offcanvas Form */}
        <div
          className={`offcanvas offcanvas-end offcanvas-large ${
            showForm ? "show" : ""
          }`}
          tabIndex={-1}
          role="dialog"
        >
          <div className="offcanvas-header border-bottom offcanvas-large-custom">
            <h5 className="fw-semibold">
              {formData?.pending_quotation_details?.status_code === "win" &&
                "View Quotation"}
              {orderTitle && "Prepare Order"}
              {!orderTitle &&
                formData?.is_order_pending == "1" &&
                selectedId &&
                "Update Quotation"}
              {formData?.is_order_pending != "1" &&
                !selectedId &&
                "Add Quotation"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                setShowForm(false);
                setOrderTitle(false);
                setFormError({});
                resetForm();
                setQuotationType("CW");
              }}
            />
          </div>

          <div className="offcanvas-body">
            <div className="row">
              <div className="col-12 col-md-4">
                <div className="mb-1" ref={setRef("quotation_type_id")}>
                  <label className="col-form-label">
                    Quotation Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    placeholder="Type"
                    name="quotation_type_id"
                    classNamePrefix="react-select"
                    className="tagging"
                    options={quotationTypeOptions}
                    value={selectedOption(
                      quotationTypeOptions,
                      formData.quotation_type_id
                    )}
                    onChange={(s) => handleSelectChange("quotation_type_id", s)}
                    isClearable
                  />
                  {formError.quotation_type_id && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.quotation_type_id}
                    </div>
                  )}
                </div>
                <div className="mb-1" ref={setRef("lead_from")}>
                  <label className="col-form-label">
                    Lead From <span className="text-danger">*</span>
                  </label>

                  <input
                    placeholder="Lead From"
                    name="lead_from"
                    className="form-control"
                    value={formData.lead_from}
                    onChange={(e) => setField("lead_from", e.target.value)}
                    required
                  />

                  {formError.lead_from && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.lead_from}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("company_id")}>
                  <label className="col-form-label">
                    Select Company <span className="text-danger">*</span>
                  </label>
                  <Select
                    placeholder="Select Company"
                    name="company_id"
                    classNamePrefix="react-select"
                    className="tagging"
                    options={companyOptions}
                    value={selectedOption(companyOptions, formData.company_id)}
                    onChange={(s) => handleSelectChange("company_id", s)}
                    isClearable
                  />
                  {formError.company_id && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.company_id}
                    </div>
                  )}
                </div>

                {/* <div className="mb-1">
                  <label className="col-form-label">
                   Notification Group
                    {" "}<span className="text-danger">*</span>
                  </label>
                  <Select
                    placeholder="Select Group"
                    name="notification_id"
                    classNamePrefix="react-select"
                    className="tagging"
                    options={notificationOptions}
                    value={selectedOption(
                      notificationOptions,
                      formData.notification_id
                    )}
                    onChange={(s) => handleSelectChange("notification_id", s)}
                    isClearable
                  />
                  {formError.notification_id && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.notification_id}
                    </div>
                  )}
                </div> */}

                <div className="mb-1" ref={setRef("contact_person")}>
                  <label className="col-form-label">
                    Contact Person <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    className="form-control"
                    placeholder="Contact Person"
                    value={formData.contact_person}
                    onChange={(e) => setField("contact_person", e.target.value)}
                  />
                  {formError.contact_person && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.contact_person}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("owner_id")}>
                  <label className="col-form-label">
                    Select Owner <span className="text-danger">*</span>
                  </label>
                  <Select
                    placeholder="Select Owner"
                    name="owner_id"
                    classNamePrefix="react-select"
                    className="tagging"
                    options={ownerOptions}
                    value={selectedOption(ownerOptions, formData.owner_id)}
                    onChange={(s) => handleSelectChange("owner_id", s)}
                    isClearable
                  />
                  {formError.owner_id && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.owner_id}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("date")}>
                  <label className="col-form-label">
                    Select Date <span className="text-danger">*</span>
                  </label>
                  <DatePicker
                    value={
                      orderTitle
                        ? dayjs(formData.date, "YYYY-MM-DD")
                        : formData?.date
                        ? dayjs(formData.date, "YYYY-MM-DD")
                        : dayjs()
                    }
                    format="DD/MM/YYYY"
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("day")
                    }
                    onChange={(date) => {
                      const formatted = date ? date.format("YYYY-MM-DD") : "";
                      setField("date", formatted);
                    }}
                    className="form-control"
                  />
                  {formError?.date && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError?.date}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("enq_ref")}>
                  <label className="col-form-label">
                    Enq Ref <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="enq_ref"
                    className="form-control"
                    placeholder="Enq Ref"
                    value={formData.enq_ref || ""}
                    onChange={(e) => setField("enq_ref", e.target.value)}
                  />
                  {formError.enq_ref && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.enq_ref}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("prepard_by")}>
                  <label className="col-form-label">
                    Prepare By <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="prepard_by"
                    className="form-control"
                    placeholder="Prepare By"
                    value={formData.prepard_by || ""}
                    onChange={(e) => setField("prepard_by", e.target.value)}
                  />
                  {formError.prepard_by && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.prepard_by}
                    </div>
                  )}
                </div>
              </div>

              <div
                className="col-12 col-md-4 form-col"
                ref={setRef("billing_address")}
              >
                <label className="col-form-label">
                  Billing Address <span className="text-danger">*</span>
                </label>
                <div className="mb-3">
                  <textarea
                    placeholder="Billing"
                    name="billing_address"
                    className="form-control"
                    style={{
                      height: "100px",
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                    value={formData.billing_address || ""}
                    onChange={(e) =>
                      setField("billing_address", e.target.value)
                    }
                  />
                  {formError.billing_address && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_address}
                    </div>
                  )}
                </div>

                {addressInfo?.other_state ? (
                  <div className="mb-1" ref={setRef("other_state")}>
                    <label className="col-form-label">
                      Other State <span className="text-danger">*</span>
                    </label>
                    <input
                      placeholder="State"
                      name="other_state"
                      type="text"
                      className="form-control"
                      value={formData.billing_other_state || ""}
                      onChange={(e) =>
                        setField("billing_other_state", e.target.value)
                      }
                    />
                  </div>
                ) : (
                  <div className="mb-1" ref={setRef("billing_state_id")}>
                    <label className="col-form-label">
                      Select State <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="billing_state_id"
                      classNamePrefix="react-select"
                      className="tagging"
                      options={stateOptions}
                      value={selectedOption(
                        stateOptions,
                        formData.billing_state_id
                      )}
                      onChange={(s) =>
                        handleSelectChange("billing_state_id", s)
                      }
                      isClearable
                      required
                    />
                    {formError.billing_state_id && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.billing_state_id}
                      </div>
                    )}
                  </div>
                )}

                <div className="mb-1" ref={setRef("billing_city")}>
                  <label className="col-form-label">
                    City <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder="City"
                    name="billing_city"
                    type="text"
                    className="form-control"
                    value={formData.billing_city || ""}
                    onChange={(e) => setField("billing_city", e.target.value)}
                  />
                  {formError.billing_city && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_city}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("billing_pin_code")}>
                  <label className="col-form-label">
                    Pin Code <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder="Pin Code"
                    name="billing_pin_code"
                    type="text"
                    className="form-control"
                    value={formData.billing_pin_code || ""}
                    onChange={(e) =>
                      setField("billing_pin_code", e.target.value)
                    }
                  />
                  {formError.billing_pin_code && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_pin_code}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("billing_mobile")}>
                  <label className="col-form-label">Mobile</label>
                  <input
                    maxLength={11}
                    placeholder="Mobile"
                    name="billing_mobile"
                    type="text"
                    className="form-control"
                    value={formData.billing_mobile || ""}
                    onChange={(e) => setField("billing_mobile", e.target.value)}
                  />
                  {formError.billing_mobile && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_mobile}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("billing_email")}>
                  <label className="col-form-label">Email</label>
                  <input
                    placeholder="Email"
                    name="billing_email"
                    type="text"
                    className="form-control"
                    value={formData.billing_email || ""}
                    onChange={(e) => setField("billing_email", e.target.value)}
                  />
                  {formError.billing_email && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_email}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("billing_landline")}>
                  <label className="col-form-label">Landline</label>
                  <input
                    placeholder="Landline"
                    name="billing_landline"
                    type="text"
                    className="form-control"
                    value={formData.billing_landline || ""}
                    onChange={(e) =>
                      setField("billing_landline", e.target.value)
                    }
                  />
                  {formError.billing_landline && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.billing_landline}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-4 form-col">
                <div className="mb-3" ref={setRef("shipping_address")}>
                  <label className="col-form-label">
                    <input
                      type="checkbox"
                      checked={sameAsBilling}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const shippingSyncLabel = selectedOption(
                          stateOptions,
                          formData.billing_state_id
                        );
                        setSameAsBilling(checked);
                        if (checked) {
                          setFormData((prev: any) => ({
                            ...prev,
                            shipping_address: prev.billing_address,
                            shipping_state_id: prev.billing_state_id,
                            shipping_other_state: prev.billing_other_state,
                            shipping_state_name: shippingSyncLabel?.label,
                            shipping_city: prev.billing_city,
                            shipping_pin_code: prev.billing_pin_code,
                            shipping_mobile: prev.billing_mobile,
                            shipping_email: prev.billing_email,
                            shipping_landline: prev.billing_landline,
                          }));
                          setFormError((prev: any) => {
                            const u = { ...prev };
                            delete u.shipping_address;
                            delete u.shipping_state_id;
                            delete u.shipping_other_state;
                            delete u.shipping_city;
                            delete u.shipping_pin_code;
                            delete u.shipping_mobile;
                            delete u.shipping_email;
                            delete u.shipping_landline;
                            return u;
                          });
                        } else {
                          setFormData((prev: any) => ({
                            ...prev,
                            shipping_address: "",
                            shipping_state_id: "",
                            shipping_other_state: "",
                            shipping_city: "",
                            shipping_pin_code: "",
                            shipping_mobile: "",
                            shipping_email: "",
                            shipping_landline: "",
                          }));
                        }
                      }}
                    />{" "}
                    Same as Billing <span className="text-danger">*</span>
                  </label>
                  {"  "}
                  <textarea
                    placeholder="Shipping"
                    name="shipping_address"
                    className="form-control"
                    style={{
                      height: "100px",
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                    value={formData.shipping_address || ""}
                    onChange={(e) =>
                      setField("shipping_address", e.target.value)
                    }
                  />
                  {formError.shipping_address && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.shipping_address}
                    </div>
                  )}
                </div>

                {addressInfo?.other_state ? (
                  <div className="mb-1" ref={setRef("shipping_other_state")}>
                    <label className="col-form-label">
                      Other State <span className="text-danger">*</span>
                    </label>
                    <input
                      placeholder="State"
                      name="shipping_other_state"
                      type="text"
                      className="form-control"
                      value={formData.shipping_other_state || ""}
                      onChange={(e) =>
                        setField("shipping_other_state", e.target.value)
                      }
                    />
                    {formError.shipping_other_state && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.shipping_other_state}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-1" ref={setRef("shipping_state_id")}>
                    <label className="col-form-label">
                      Select State <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="shipping_state_id"
                      classNamePrefix="react-select"
                      className="tagging"
                      options={stateOptions}
                      value={selectedOption(
                        stateOptions,
                        formData.shipping_state_id
                      )}
                      onChange={(s) =>
                        handleSelectChange("shipping_state_id", s)
                      }
                      isClearable
                    />
                    {formError.shipping_state_id && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.shipping_state_id}
                      </div>
                    )}
                  </div>
                )}

                <div className="mb-1" ref={setRef("shipping_city")}>
                  <label className="col-form-label">
                    City <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder="City"
                    name="shipping_city"
                    type="text"
                    className="form-control"
                    value={formData.shipping_city || ""}
                    onChange={(e) => setField("shipping_city", e.target.value)}
                  />
                  {formError.shipping_city && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.shipping_city}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("shipping_pin_code")}>
                  <label className="col-form-label">
                    Pin Code <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder="Pin Code"
                    name="shipping_pin_code"
                    type="text"
                    className="form-control"
                    value={formData.shipping_pin_code || ""}
                    onChange={(e) =>
                      setField("shipping_pin_code", e.target.value)
                    }
                  />
                  {formError.shipping_pin_code && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.shipping_pin_code}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("shipping_mobile")}>
                  <label className="col-form-label">
                    Mobile <span className="text-danger">*</span>
                  </label>
                  <input
                    maxLength={11}
                    placeholder="Mobile"
                    name="shipping_mobile"
                    type="text"
                    className="form-control"
                    value={formData.shipping_mobile || ""}
                    onChange={(e) =>
                      setField("shipping_mobile", e.target.value)
                    }
                  />
                  {formError.shipping_mobile && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.shipping_mobile}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("shipping_email")}>
                  <label className="col-form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder="Email"
                    name="shipping_email"
                    type="text"
                    className="form-control"
                    value={formData.shipping_email || ""}
                    onChange={(e) => setField("shipping_email", e.target.value)}
                  />
                  {formError.shipping_email && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.shipping_email}
                    </div>
                  )}
                </div>

                <div className="mb-1" ref={setRef("shipping_landline")}>
                  <label className="col-form-label">
                    Landline <span className="text-danger">*</span>
                  </label>
                  <input
                    placeholder="Landline"
                    name="shipping_landline"
                    type="text"
                    className="form-control"
                    value={formData.shipping_landline || ""}
                    onChange={(e) =>
                      setField("shipping_landline", e.target.value)
                    }
                  />
                  {formError.shipping_landline && (
                    <div className="invalid-feedback d-block text-danger">
                      {formError.shipping_landline}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-4 mb-2">
                <label className="col-form-label">
                  Search Product <span className="text-danger">*</span>
                </label>
                <AsyncSelect<ProductOption, false>
                  key={productRows.length}
                  cacheOptions
                  defaultOptions={false}
                  loadOptions={loadProductOptions}
                  placeholder="Search Product"
                  classNamePrefix="react-select"
                  className="tagging"
                  name="product_id"
                  value={null}
                  onChange={(s) => handleSelectChangeFilter("product_id", s)}
                  isClearable
                />
              </div>
              <div className="col-12 col-md-4 mb-2" ref={setRef("currency_id")}>
                <label className="col-form-label">
                  Select Currency <span className="text-danger">*</span>
                </label>
                <Select
                  isDisabled={orderTitle}
                  placeholder="Select Currency"
                  name="currency_id"
                  classNamePrefix="react-select"
                  className="tagging"
                  options={currencyOptions}
                  value={selectedOption(currencyOptions, formData.currency_id)}
                  onChange={(s) => handleSelectChange("currency_id", s)}
                  isClearable
                />
                {formError.currency_id && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.currency_id}
                  </div>
                )}
              </div>

              <div
                className="col-12 col-md-4 mb-2"
                ref={setRef("delivery_date_id")}
              >
                <label className="col-form-label">
                  Payment Terms <span className="text-danger">*</span>
                </label>

                {String(formData.delivery_date_id) === "7" ? (
                  <input
                    className="form-control"
                    placeholder="Specify"
                    value={formData.delivery_date_custom || ""}
                    onChange={(e) => {
                      setFormData((p: any) => ({
                        ...p,
                        delivery_date_custom: e.target.value,
                      }));
                      setFormError((e: any) => ({
                        ...e,
                        delivery_date_id: "",
                      }));
                    }}
                    onBlur={() => {
                      if (!formData.delivery_date_custom) {
                        setFormData((p: any) => ({
                          ...p,
                          delivery_date_id: "",
                        }));
                        setFormError((e: any) => ({
                          ...e,
                          delivery_date_id: "",
                        }));
                      }
                    }}
                  />
                ) : (
                  <Select
                    placeholder="Select Term"
                    name="delivery_date_id"
                    classNamePrefix="react-select"
                    className="tagging"
                    isClearable
                    options={paymentAdvanceDate.map((x) => ({
                      value: x.id,
                      label: x.name,
                    }))}
                    value={
                      formData.delivery_date_id
                        ? paymentAdvanceDate
                            .map((x) => ({ value: x.id, label: x.name }))
                            .find(
                              (x) =>
                                String(x.value) ===
                                String(formData.delivery_date_id)
                            ) || null
                        : null
                    }
                    onChange={(opt) => {
                      if (!opt) {
                        setFormData((p: any) => ({
                          ...p,
                          delivery_date_id: "",
                          delivery_date_custom: "",
                          delivery_term_label: "",
                        }));
                        setFormError((e: any) => ({
                          ...e,
                          delivery_date_id: "",
                        }));
                        return;
                      }

                      const deliveryPeriod = resolveDeliveryPeriod(opt.value);

                      setFormData((p: any) => ({
                        ...p,
                        delivery_date_id: String(opt.value),
                        delivery_date_custom:
                          String(opt.value) === "7" ? "" : deliveryPeriod,
                        delivery_term_data: opt.label,
                        delivery_term_label:
                          String(opt.value) === "7" ? "" : opt.label,
                      }));

                      setFormError((e: any) => ({
                        ...e,
                        delivery_date_id: "",
                      }));
                    }}
                  />
                )}

                {formError.delivery_date_id && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.delivery_date_id}
                  </div>
                )}
              </div>
            </div>

            {/* product search + rows */}
            <div className="row">
              {window.innerWidth > 768 && orderTitle && (
                <>
                  <div
                    className="col-12 col-md-3 mb-2"
                    ref={setRef("courier_id")}
                  >
                    <label className="col-form-label">
                      Select Courier <span className="text-danger">*</span>
                    </label>
                    <Select
                      placeholder="Select Courier"
                      name="courier_id"
                      classNamePrefix="react-select"
                      className="tagging"
                      options={courierOptions}
                      value={selectedOption(
                        courierOptions,
                        formData.courier_id
                      )}
                      onChange={(s) => handleSelectChange("courier_id", s)}
                      isClearable
                    />
                    {formError.courier_id && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.courier_id}
                      </div>
                    )}
                  </div>

                  <div
                    className="col-12 col-md-3 mb-2"
                    ref={setRef("customer_order_no")}
                  >
                    <div className="mb-1">
                      <label className="col-form-label">
                        Customer Order No.{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="customer_order_no"
                        className="form-control"
                        placeholder="Customer Order NO"
                        value={formData.customer_order_no || ""}
                        onChange={(e) =>
                          setField("customer_order_no", e.target.value)
                        }
                      />
                      {formError.customer_order_no && (
                        <div className="invalid-feedback d-block text-danger">
                          {formError.customer_order_no}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className="col-12 col-md-3 mb-2"
                    ref={setRef("overdue_no")}
                  >
                    <div className="mb-1">
                      <label className="col-form-label">Overdue No.</label>
                      <input
                        type="text"
                        name="overdue_no"
                        className="form-control"
                        placeholder="Overdues Number"
                        value={formData.overdue_no || ""}
                        onChange={(e) => setField("overdue_no", e.target.value)}
                      />
                      {formError.overdue_no && (
                        <div className="invalid-feedback d-block text-danger">
                          {formError.overdue_no}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className="col-12 col-md-3 mb-2"
                    ref={setRef("overdues_value")}
                  >
                    <div className="mb-1">
                      <label className="col-form-label">Overdue Value</label>
                      <input
                        type="text"
                        name="overdues_value"
                        className="form-control"
                        placeholder="Overdues Value"
                        value={formData.overdues_value || ""}
                        onChange={(e) =>
                          setField("overdues_value", e.target.value)
                        }
                      />
                      {formError.overdues_value && (
                        <div className="invalid-feedback d-block text-danger">
                          {formError.overdues_value}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div
                style={{
                  borderRadius: "14px",
                  padding: "14px",
                  overflow: "auto",
                }}
              >
                <table
                  className="table table-view"
                  style={{
                    tableLayout: "auto",
                    fontSize: "12px",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  }}
                >
                  {/* ================= HEADER ================= */}
                  <thead>
                    <tr>
                      {[
                        "Part No.",
                        "Description",
                        ...(quotationType == "GW"
                          ? ["Principal", "UOM", "Specification & Heading"]
                          : []),
                        "HSN",
                        "Qty",
                        "In Stock",
                        `Price [${currencyName}]`,
                        "Disc %",
                        "Net Price",
                        "IGST %",
                        "Total",
                        "Notes",
                        "Action",
                      ].map((h, i) => (
                        <th
                          key={i}
                          style={{
                            // position: "sticky",
                            top: 0,
                            zIndex: 1,
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: ".04em",
                            textAlign: "center",
                            padding: "10px 8px",
                            borderBottom: "1px solid #e5e7eb",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  {/* ================= BODY ================= */}
                  <tbody>
                    {productRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={15}
                          style={{ padding: "24px", textAlign: "center" }}
                        >
                          <b>Note:</b> No products added. Use search to add
                          products.
                          {formError.product_list && (
                            <div className="text-danger mt-2">
                              At least one product is required
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : (
                      productRows.map((row, index) => (
                        <React.Fragment key={index}>
                          <tr key={index}>
                            {(
                              [
                                "part_no",
                                "description",
                                ...(quotationType == "GW"
                                  ? ["principal", "uom", "specification"]
                                  : []),
                                "hsn_code",
                                "quantity",
                                "in_stock",
                                "price",
                                "discount",
                                "net_price",
                                "igst",
                                "total",
                                "notes",
                              ] as const
                            ).map((field) => {
                              const widths: Record<string, string> = {
                                part_no: "240px",
                                description: "20px",
                                principal: "150px",
                                uom: "70px",
                                specification: "20px",
                                hsn_code: "120px",
                                quantity: "90px",
                                in_stock: "20px",
                                price: "100px",
                                discount: "40px",
                                net_price: "100px",
                                igst: "50px",
                                total: "100px",
                                notes: "30px",
                              };

                              return (
                                <td
                                  key={field}
                                  style={{
                                    minWidth: widths[field],
                                    maxWidth: widths[field],
                                    padding: "2px",
                                    verticalAlign: "middle",
                                    borderBottom: "1px solid #f1f5f9",
                                  }}
                                >
                                  {[
                                    "description",
                                    "specification",
                                    "notes",
                                    "product_description",
                                  ].includes(field) ? (
                                    <button
                                      className="btn btn-sm-outline w-100"
                                      style={{
                                        borderRadius: "8px",
                                        border: "1px dashed #cbd5e1",
                                        background: "#f8fafc",
                                      }}
                                      disabled={
                                        formData?.pending_quotation_details
                                          ?.status_code === "win"
                                      }
                                      onClick={() =>
                                        openEditor(index, field as any)
                                      }
                                    >
                                      {row[field] != "" &&
                                      row[field] != undefined ? (
                                        <>
                                          <i className="ti ti-pencil" />
                                        </>
                                      ) : (
                                        <>
                                          <i className="ti ti-plus" />
                                        </>
                                      )}
                                    </button>
                                  ) : field === "quantity" ? (
                                    <div className="input-group input-group-sm">
                                      <button
                                        className="btn btn-outline-success"
                                        disabled={
                                          numberOrZero(row[field]) === 1
                                        }
                                        onClick={() =>
                                          updateRow(
                                            index,
                                            field,
                                            numberOrZero(row[field]) - 1
                                          )
                                        }
                                      >
                                        −
                                      </button>

                                      <input
                                        className="form-control text-center"
                                        value={row[field] ?? 0}
                                        onChange={(e) =>
                                          updateRow(
                                            index,
                                            field,
                                            numberOrZero(e.target.value)
                                          )
                                        }
                                      />
                                      <button
                                        className="btn btn-outline-success"
                                        onClick={() =>
                                          updateRow(
                                            index,
                                            field,
                                            numberOrZero(row[field]) + 1
                                          )
                                        }
                                      >
                                        +
                                      </button>
                                    </div>
                                  ) : (
                                    <input
                                      className="form-control form-control-md"
                                      disabled={
                                        formData?.pending_quotation_details
                                          ?.status_code === "win"
                                      }
                                      readOnly={[
                                        "part_no",
                                        "net_price",
                                        "in_stock",
                                        "total",
                                        "uom",
                                        "hsn_code",
                                      ].includes(field)}
                                      value={
                                        field === "principal"
                                          ? typeof row[field] === "object"
                                            ? row[field]?.type ?? ""
                                            : row[field] ?? ""
                                          : field === "uom"
                                          ? typeof row[field] === "object"
                                            ? row[field]?.uom ?? ""
                                            : row[field] ?? ""
                                          : row[field] ?? ""
                                      }
                                      onChange={(e) =>
                                        updateRow(index, field, e.target.value)
                                      }
                                      style={{
                                        fontSize: "12px",
                                        background: [
                                          "net_price",
                                          "total",
                                        ].includes(field)
                                          ? "#f8fafc"
                                          : "#ffffff",
                                        borderRadius: "8px",
                                      }}
                                    />
                                  )}
                                </td>
                              );
                            })}

                            <td
                              className="d-flex gap-1"
                              style={{ justifyContent: "center" }}
                            >
                              <button
                                disabled={
                                  formData?.pending_quotation_details
                                    ?.status_code == "win"
                                }
                                type="button"
                                className="btn btn-icon btn-sm btn-soft-secondary"
                                onClick={() =>
                                  openEditor(index, "Comments" as any)
                                }
                                title="Add Comments"
                              >
                                <i className="fa fa-comment" />
                              </button>
                              <button
                                className="btn btn-icon btn-sm btn-soft-danger"
                                disabled={
                                  productRows.length === 1 ||
                                  formData?.pending_quotation_details
                                    ?.status_code === "win"
                                }
                                onClick={() => deleteProduct(index)}
                              >
                                <i className="feather-trash" />
                              </button>
                            </td>
                          </tr>
                          {/* {row.showDescriptionEditor && (
                            <tr>
                              <td colSpan={13}>
                                <QuillEditor
                                  value={row.product_specification}
                                  onChange={(html: any) => {
                                    setProductRows((prev) => {
                                      const updated = [...prev];
                                      updated[index].product_specification =
                                        html;
                                      return updated;
                                    });
                                  }}
                                  placeholder="Write Comment ..."
                                  height={70}
                                />
                                {formError.product_list?.[index]
                                  ?.product_specification && (
                                  <div className="invalid-feedback d-block text-danger">
                                    {
                                      formError.product_list[index]
                                        .product_specification
                                    }
                                  </div>
                                )}
                              </td>
                            </tr>
                          )} */}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>

                  {/* ================= FOOTER ================= */}
                  {!isMobile && productRows.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={8}></td>
                        <td
                          colSpan={quotationType == "GW" ? 5 : 1}
                          style={{
                            textAlign: "right",
                            fontWeight: 600,
                            padding: "12px",
                            borderTop: "2px solid #e5e7eb",
                          }}
                        >
                          Sub Total
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            fontWeight: 700,
                          }}
                        >
                          {subTotal.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {window.innerWidth <= 768 && orderTitle && (
                <>
                  <div className="col-12 col-md-2">
                    <label className="col-form-label">
                      Select Courier <span className="text-danger">*</span>
                    </label>
                    <Select
                      placeholder="Select Courier"
                      name="courier_id"
                      classNamePrefix="react-select"
                      className="tagging"
                      options={courierOptions}
                      value={selectedOption(
                        courierOptions,
                        formData.courier_id
                      )}
                      onChange={(s) => handleSelectChange("courier_id", s)}
                      isClearable
                    />
                    {formError.courier_id && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.courier_id}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-md-2">
                    <div className="mb-1">
                      <label className="col-form-label">
                        Customer Order No.{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="customer_order_no"
                        className="form-control"
                        placeholder="Customer Order NO"
                        value={formData.customer_order_no || ""}
                        onChange={(e) =>
                          setField("customer_order_no", e.target.value)
                        }
                      />
                      {formError.customer_order_no && (
                        <div className="invalid-feedback d-block text-danger">
                          {formError.customer_order_no}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-md-2">
                    <div className="mb-1">
                      <label className="col-form-label">
                        Overdue No. <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="overdue_no"
                        className="form-control"
                        placeholder="Overdues Number"
                        value={formData.overdue_no || ""}
                        onChange={(e) => setField("overdue_no", e.target.value)}
                      />
                      {formError.overdue_no && (
                        <div className="invalid-feedback d-block text-danger">
                          {formError.overdue_no}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-md-2">
                    <div className="mb-1">
                      <label className="col-form-label">
                        Overdue Value <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="overdues_value"
                        className="form-control"
                        placeholder="Overdues Value"
                        value={formData.overdues_value || ""}
                        onChange={(e) =>
                          setField("overdues_value", e.target.value)
                        }
                      />
                      {formError.overdues_value && (
                        <div className="invalid-feedback d-block text-danger">
                          {formError.overdues_value}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="row col-12" style={{ marginTop: "-30px" }}>
              {/* Payment Notes and Extra Notes: switch to stacked full-width on mobile */}
              <div className={"col-6"}>
                <div className="d-flex align-items-center">
                  <label className="col-form-label mb-0">
                    Terms & Condition <span className="text-danger">*</span>
                  </label>

                  {quotationType === "GW" && (
                    <label className="col-form-label mb-0 ms-3">
                      <input
                        type="checkbox"
                        className="me-2"
                        checked={formData.show_note}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            show_note: e.target.checked,
                          })
                        }
                      />
                      Do you want to show notes ?
                    </label>
                  )}
                </div>
                <div style={isMobile ? { maxWidth: "100%" } : {}}>
                  <QuillEditor
                    theme="snow"
                    value={formData.payment_term_condition}
                    onChange={(html: string) => {
                      setField("payment_term_condition", html);
                    }}
                    placeholder="Payment Term And Conditions"
                    height={isMobile ? 180 : 100}
                    // If QuillEditor accepts className, update it to receive "quill-mobile" for mobile
                  />
                </div>
                {formError.payment_term_condition && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.payment_term_condition}
                  </div>
                )}
              </div>

              <div className={"col-6"}>
                <label className="col-form-label mt-0">Extra Notes</label>
                <textarea
                  placeholder="write !"
                  name="product_description"
                  className="form-control"
                  style={{
                    height: isMobile ? 160 : 165,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                  value={formData.product_description || ""}
                  onChange={(e) =>
                    setField("product_description", e.target.value)
                  }
                />
                {/* {formError.product_description && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.product_description}
                  </div>
                )} */}
              </div>
            </div>

            {formData?.pending_quotation_details?.status_code !== "win" && (
              <div
                className="d-flex justify-content-end"
                style={{ marginTop: "10px" }}
              >
                {/* PREVIEW BUTTON (added) */}
                {/* <button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  onClick={() => setShowPreview(true)}
                >
                  Preview
                </button> */}

                <button
                  type="button"
                  className="btn btn-soft-primary me-2"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                    setOrderPreviewTrue(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  // onClick={() => setShowPreview(true)}
                  onClick={() =>
                    handleSubmit(
                      orderPreviewTrue ? "order_preview" : "quotation_preview"
                    )
                  }
                  className="btn btn-soft-success"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {orderTitle && selectedId
                        ? "Prepare Order..."
                        : selectedId
                        ? "Updating..."
                        : "Creating..."}
                    </>
                  ) : orderTitle && selectedId ? (
                    "Preview Order"
                  ) : selectedId ? (
                    "Preview Quotation"
                  ) : (
                    "Preview Quotation"
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* {specEditorIndex !== null && (
            <div
              className="modal fade show d-block"
              style={{ background: "rgba(0,0,0,.4)" }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Product Specification</h5>
                    <button
                      className="btn-close"
                      onClick={closeSpecificationEditor}
                    />
                  </div>

                  <div className="modal-body">
                    <QuillEditor
                      value={productRows[specEditorIndex]?.product_specification}
                      onChange={(html: any) => {
                        setProductRows((prev) => {
                          const updated = [...prev];
                          updated[specEditorIndex].product_specification = html;
                          return updated;
                        });
                      }}
                      placeholder="Write specification..."
                      height={200}
                    />
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={closeSpecificationEditor}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={closeSpecificationEditor}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )} */}

        {/* {descEditorIndex !== null && (
          <div
            className="modal fade show d-block"
            style={{ background: "rgba(0,0,0,.4)" }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Product Description</h5>
                  <button
                    className="btn-close"
                    onClick={closeDescriptionEditor}
                  />
                </div>

                <div className="modal-body">
                  <QuillEditor
                    value={productRows[descEditorIndex]?.description}
                    onChange={(html: any) => {
                      setProductRows((prev) => {
                        const updated = [...prev];
                        updated[descEditorIndex].description = html;
                        return updated;
                      });
                    }}
                    placeholder="Write description..."
                    height={200}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={closeDescriptionEditor}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={closeDescriptionEditor}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )} */}

        {editor && (
          <div
            className="modal fade show d-block"
            style={{ background: "rgba(0,0,0,.4)" }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editor.field === "product_specification"
                      ? "COMMENTS"
                      : editor.field.replace("_", " ").toUpperCase()}{" "}
                    {`( ROW => ${editor.index + 1} )`}
                  </h5>
                  <button className="btn-close" onClick={closeEditor} />
                </div>

                <div className="modal-body">
                  {editor.field === "notes" ||
                  editor.field === "description" ? (
                    <textarea
                      className="form-control"
                      rows={8}
                      value={editorValue}
                      onChange={(e) => setEditorValue(e.target.value)}
                    />
                  ) : (
                    <QuillEditor
                      value={editorValue}
                      onChange={(html: string) => setEditorValue(html)}
                      height={200}
                      placeholder="Write ..."
                    />
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-soft-primary"
                    onClick={closeEditor}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-soft-success"
                    onClick={saveEditorValue}
                  >
                    Save
                  </button>{" "}
                </div>
              </div>
            </div>
          </div>
        )}

        <Reason
          id="messageModal"
          title="Quotation Status Update"
          show={showMessageModal}
          onHide={() => setShowMessageModal(false)}
          pendingSubmitting={pendingSubmitting}
          footer={
            !["closed", "lose", "win"].includes(
              quotationReason?.pending_quotation_details?.status_code
            ) && (
              <>
                <button
                  type="button"
                  className="btn btn-soft-danger"
                  disabled={pendingSubmitting}
                  onClick={() => setShowMessageModal(false)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="btn btn-soft-success"
                  onClick={updateReasonStatus}
                  disabled={pendingSubmitting}
                >
                  {pendingSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Save Comment
                    </>
                  ) : (
                    "Save Comment"
                  )}
                </button>
              </>
            )
          }
        >
          <form>
            <div className="mb-1">
              <label className="form-label">Quotation No :</label>
              <input
                readOnly
                type="text"
                className="form-control"
                value={
                  quotationReason?.pending_quotation_details
                    ?.unique_quotation_no || ""
                }
                style={{ background: "#F6F6F6" }}
              />
            </div>

            <div className="mb-1">
              <label className="form-label">Last Follow up Date :</label>
              <input
                readOnly
                type="text"
                className="form-control"
                value={
                  quotationReason?.pending_quotation_details?.last_updated_at ||
                  ""
                }
                style={{ background: "#F6F6F6" }}
              />
            </div>

            <div className="mb-1">
              <label className="form-label">Quotation Value :</label>
              <input
                readOnly
                type="text"
                className="form-control"
                value={
                  quotationReason?.pending_quotation_details?.total_amount || ""
                }
                style={{ background: "#F6F6F6" }}
              />
            </div>

            <div className="mb-1">
              <label className="form-label">Email Id :</label>
              <input
                readOnly
                type="text"
                className="form-control"
                value={quotationReason?.company_details?.email_id || ""}
                style={{ background: "#F6F6F6" }}
              />
            </div>

            <div className="mb-1">
              <label htmlFor="recipient-name" className="form-label">
                Current follow up Date :
              </label>
              <DatePicker
                disabled={isStatusLocked || pendingSubmitting}
                value={
                  formDataPendingQuotation.current_follow_date
                    ? dayjs(
                        formDataPendingQuotation.current_follow_date,
                        "YYYY-MM-DD"
                      )
                    : dayjs()
                }
                format="DD-MM-YYYY"
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                onChange={(date) => {
                  const formatted = date ? date.format("YYYY-MM-DD") : "";
                  setPendingField(
                    "current_follow_date",
                    formatted || dayjs().format("YYYY-MM-DD")
                  );
                }}
                className="form-control"
                style={{
                  background:
                    isStatusLocked || pendingSubmitting ? "#F6F6F6" : undefined,
                }}
              />
              {formErrorPendingQuotation.current_follow_date && (
                <div className="invalid-feedback d-block text-danger">
                  {formErrorPendingQuotation.current_follow_date}
                </div>
              )}
            </div>

            <div className="mb-1">
              <label className="form-label">Quotation Status :</label>
              <Select
                isDisabled={
                  ["closed", "lose", "win"].includes(
                    quotationReason?.pending_quotation_details?.status_code
                  ) || pendingSubmitting
                }
                placeholder="Select Status"
                options={statusOptions}
                value={selectedOption(
                  statusOptions,
                  Number(formDataPendingQuotation.reason_status_id) || null
                )}
                onChange={(s: any) => {
                  setPendingField("reason_status_id", s?.value ?? "");
                }}
                isClearable
                classNamePrefix="react-select"
              />
              {formErrorPendingQuotation.reason_status_id && (
                <div className="invalid-feedback d-block text-danger">
                  {formErrorPendingQuotation.reason_status_id}
                </div>
              )}
            </div>

            <div className="mb-1">
              <label className="form-label">Add Reason :</label>
              <textarea
                name="reason"
                readOnly={
                  ["closed", "lose", "win"].includes(
                    quotationReason?.pending_quotation_details?.status_code
                  ) || pendingSubmitting
                }
                className="form-control"
                value={formDataPendingQuotation.reason || ""}
                placeholder={descReason || "No existing reason"}
                onChange={(e) => setPendingField("reason", e.target.value)}
                style={{
                  height: "150px",
                  background:
                    ["closed", "lose", "win"].includes(
                      quotationReason?.pending_quotation_details?.status_code
                    ) || pendingSubmitting
                      ? "#F6F6F6"
                      : undefined,
                }}
              />
              {formErrorPendingQuotation.reason && (
                <div className="invalid-feedback d-block text-danger">
                  {formErrorPendingQuotation.reason}
                </div>
              )}
            </div>
          </form>
        </Reason>

        <Success
          id="formatSuccessModal"
          title={orderTitle ? "Order" : "Quotation"}
          message={showMessage}
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
        />

        <Delete
          show={showDeleteModal}
          title="Delete Quotation?"
          message="Are you sure?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />

        {/* Quotation Preview modal (component) */}
        <QuotationPreview
          show={showPreview}
          onClose={() => setShowPreview(false)}
          onSubmit={() => handleSubmit("submit")}
          formData={formData}
          productRows={productRows}
          currencyName={currencyName}
          headerLogoUrl="assets/img/logo.png"
          footerImageUrl="assets/img/bannerImg2.png"
          quotationFormat={quotationFormat}
        />
        <OrderPreview
          show={showOrderPreview}
          onClose={() => setOrderShowPreview(false)}
          onSubmit={() => handleSubmit("submit")}
          formData={formData}
          productRows={productRows}
          currencyName={currencyName}
          headerLogoUrl="assets/img/logo.png"
          footerImageUrl="assets/img/bannerImg2.png"
          quotationFormat={quotationFormat}
        />
      </div>
    </div>
  );
};

export default Quotation;
